import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts';

interface Props {
  remainingAttempts: number;
  onStartClick: () => void;
}

const WelcomeCard: React.FC<Props> = ({ remainingAttempts, onStartClick }) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();

  const sections = [
    { name: t('dashboard.vocabulary'), desc: t('dashboard.questions') },
    { name: t('dashboard.synonyms'), desc: t('dashboard.questions') },
    { name: t('dashboard.analogies'), desc: t('dashboard.questions') },
    { name: t('dashboard.comprehension'), desc: t('dashboard.questions') },
  ];

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <h1 className="text-2xl font-bold text-primary text-center mb-1">{t('dashboard.welcome')}</h1>
      
      <p className="text-gray-600 text-center mb-6">
        {t('dashboard.subtitle')}
      </p>

      {/* Test info */}
      <div className="bg-secondary bg-opacity-10 p-5 rounded-lg mb-6">
        <div className="grid grid-cols-3 gap-4 max-w-md mx-auto text-center">
          <div>
            <p className="text-2xl font-bold text-secondary">40</p>
            <p className="text-xs text-gray-600 mt-1">{t('dashboard.questions')}</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-secondary">12 min</p>
            <p className="text-xs text-gray-600 mt-1">{t('dashboard.duration')}</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-secondary">3</p>
            <p className="text-xs text-gray-600 mt-1">{t('dashboard.languages')}</p>
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('dashboard.sections')}</h3>
        <div className="space-y-2">
          {sections.map((s, i) => (
            <div key={i} className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-secondary rounded-full" />
                <p className="text-sm text-gray-700">{s.name}</p>
              </div>
              <p className="text-xs text-gray-400">10 {s.desc.toLowerCase()}</p>
            </div>
          ))}
        </div>
      </div>

      {/* What you need */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('instructions.beforeStarting')}</h3>
        <div className="space-y-2">
          {(t('instructions.beforeStartingItems', { returnObjects: true }) as string[]).map((item: string, i: number) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-2 h-2 bg-secondary rounded-full flex-shrink-0" />
              <p className="text-sm text-gray-700">{item}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Action */}
      <div className="border-t pt-5">
        <div className="text-center mb-4">
          <p className="text-gray-600 text-sm mb-1">{t('dashboard.remainingTests')}</p>
          <p className="font-bold text-primary text-3xl">{remainingAttempts}</p>
        </div>
        <button
          onClick={onStartClick}
          disabled={remainingAttempts === 0}
          className="w-full bg-secondary hover:bg-opacity-90 text-white font-bold py-3 rounded-lg text-base transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('instructions.startNow')}
        </button>
        {remainingAttempts === 0 && (
          <p className="text-red-600 text-sm mt-3 text-center font-medium">
            {t('dashboard.noAttempts')}
          </p>
        )}
      </div>
    </div>
  );
};

export default WelcomeCard;
